import * as Yup from "yup";
import { forwardRef } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { userRole } from "@/type/CommonTypes";
import { cn } from "@/lib/utils";
import { Trash2 } from "lucide-react";
import { type CommentItemModel } from "../board-commentlist";
import BoardCommentForm from "../BoardCommentForm/BoardCommentForm";
import { DateUtils } from "@/utils/dateUtil";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { requestHandler } from "@/utils/apiUtils";
import { toast } from "sonner";
import SupabasePool from "@/lib/supabaseClient";
import Comment from "@/asset/comment.svg?react";
import FadeInAnimation from "@/components/animations/FadeInAnimation";
import BadgeIsNew from "@/components/ui/badge-isnew.";
import { useCommentForm } from "@/hooks/useCommentForm";
import { useAuthentication } from "@/hooks/useAuth";
export interface BoardCommentProps {
  item: CommentItemModel;
  deps: number;
  rootId?: number;
}

const BoardComment = forwardRef<HTMLDivElement, BoardCommentProps>(
  ({ item, deps, rootId }, ref) => {
    const authentication = useAuthentication();
    const { viewId, toggleId } = useCommentForm();
    const pool = SupabasePool.getInstance();
    const queryClient = useQueryClient();

    const { id, comment, author, children, created_at } = item;

    const schema = Yup.object({
      password: authentication
        ? Yup.string().notRequired()
        : Yup.string().required("비밀번호를 입력해주세요."),
    });

    const { formState, reset } = useForm({
      resolver: yupResolver(schema),
      defaultValues: {
        password: "",
      },
    });

    const { mutate } = useMutation({
      mutationFn: async () => {
        return await requestHandler(async () => {
          const { data: session } = await pool.auth.getSession();
          if (!session) throw new Error("권한이 없습니다.");

          const { error, data } = await pool
            .from("guest_board")
            .delete()
            .eq("id", id)
            .select();

          if (error) {
            throw new Error(`삭제 실패: ${error.message}`);
          }

          if (!data || data.length === 0) {
            throw new Error("삭제되지 않았습니다.");
          }
          return { data: true };
        });
      },
      onSuccess: async () => {
        toast.success("삭제 되었습니다");
        reset();
        await queryClient.invalidateQueries({
          queryKey: ["GUESTBOARD"],
        });
      },
    });

    const onDeleteHandler = () => {
      if (!confirm("삭제하시겠습니까?")) return;
      mutate();
    };

    const isAdmin = author.role === userRole.SUPER;

    return (
      <FadeInAnimation>
        <div
          ref={ref}
          className={cn("group relative ", deps === 1 ? "mb-6 " : "mb-4 ")}
        >
          {/* 메인 댓글 컨테이너 */}
          <div className="flex gap-3">
            {/* 아바타 - 고정 너비 */}
            <div className="shrink-0 w-8">
              <div className="relative size-8 rounded-full overflow-hidden ring-2 ring-zinc-600/60 transition-all">
                <img
                  src={
                    isAdmin
                      ? "/img/my-dog.webp"
                      : `/img/board/${author.profile_img}`
                  }
                  alt={author.nickname}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* 댓글 내용 영역 */}
            <div className="flex-1 min-w-0">
              {/* 헤더 - 한 줄로 정렬 */}
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-sm font-medium text-zinc-200">
                  {author.nickname}
                </span>

                {isAdmin && (
                  <span className=" py-0.5 text-[10px] font-semibold text-pink-400 rounded">
                    ADMIN
                  </span>
                )}

                <span className="text-xs text-zinc-500">
                  {DateUtils.fromNow(created_at)}
                </span>
                <BadgeIsNew createAt={created_at} />

                {/* 삭제 버튼 - 우측 정렬 */}
                {authentication && (
                  <button
                    onClick={onDeleteHandler}
                    className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity p-1.5 hover:bg-red-500/10 rounded text-zinc-600 hover:text-red-400"
                    aria-label="댓글 삭제"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>

              {/* 댓글 내용 */}
              <div
                className={cn(
                  "text-[13px] md:text-sm text-zinc-50 leading-relaxed whitespace-pre-wrap wrap-break-word mb-2 bg-zinc-50/5 border border-border inline-block p-2 rounded-lg"
                )}
              >
                {comment}
              </div>

              {/* 액션 버튼 영역 */}
              <div className="flex items-center gap-3 ">
                <button
                  className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50 transition-all"
                  onClick={() => toggleId(id)}
                >
                  <Comment className="size-3.5 [&>path]:fill-white/20" />
                  <span>댓글쓰기</span>
                  {deps === 1 && children.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 text-[11px] bg-zinc-800 text-zinc-300 rounded-full">
                      {children.length}
                    </span>
                  )}
                </button>
              </div>

              {/* 에러 메시지 */}
              {formState.errors.password && (
                <p className="mt-2 text-xs text-red-400">
                  {formState.errors.password.message}
                </p>
              )}

              {/* 답글 폼 */}
              {viewId === id && (
                <div className=" border-t border-zinc-800/50 mb-4">
                  <BoardCommentForm parent_id={rootId} />
                </div>
              )}
            </div>
          </div>

          {/* 대댓글 영역 */}
          {children && children.length > 0 && (
            <div className="mt-3 ml-8 md:ml-17 space-y-3 relative py-2">
              {/* 연결선 */}
              <div className="absolute left-4 md:left-[-22px] top-0 bottom-0 w-0.5 bg-linear-to-b from-zinc-600/30 to-transparent" />

              {children.map((child) => (
                <div
                  key={`${child.parent_id}:${child.id}`}
                  className="relative"
                >
                  {/* 개별 연결선 */}
                  <div className="absolute -left-4 md:left-[-22px] top-4 w-3 md:w-4 h-0.5 bg-zinc-600/30" />

                  <BoardComment item={child} deps={deps + 1} rootId={id} />
                </div>
              ))}
            </div>
          )}
        </div>
      </FadeInAnimation>
    );
  }
);

BoardComment.displayName = "BoardComment";

export default BoardComment;
